<template>
  <div class="borrow">
    <div class="box box-primary">
      <div class="box-header with-border">
        <h3 class="box-title">
          {{ $t('menu.cancelled') }}
        </h3>
        <div class="pull-right box-tools d-sm-none d-md-block">
          <template v-if="can([$Permissions.backend.cancelled.export])">
            <a
              :href="`${baseUrl}/export/applicant/cancelled`"
              class="btn btn-info btn-sm"
            >
              <i class="fa fa-file-excel-o" /> {{ $t('label.exportAsExcel') }}
            </a>
          </template>
        </div>
      </div>
      <div class="box-body">
        <div class="row">
          <div class="dataTables_row_up">
            <div class="col-xs-8 col-sm-6 margin-bottom">
              <div class="d-flex">
                <div class="margin-right-5">
                  <div class="btn-group mr-2">
                    <button
                      class="btn btn-default"
                      type="button"
                    >
                      {{ $t('label.showOrHideColumns') }}
                    </button>
                    <button
                      aria-expanded="false"
                      aria-haspopup="true"
                      class="btn btn-default dropdown-toggle"
                      data-toggle="dropdown"
                      type="button"
                    >
                      <span class="caret" />
                      <span class="sr-only">Toggle Dropdown</span>
                    </button>
                    <ul
                      aria-labelledby="showColumnRow"
                      class="dropdown-menu"
                    >
                      <template v-for="(column, columnIndex) in columns">
                        <li
                          :key="columnIndex"
                          @click="toggleColumn(columnIndex)"
                        >
                          <a
                            :class="`${(!oTable || oTable.columns().visible()[columnIndex]) ? 'text-blue' : 'text-red'}`"
                            href="javascript:void(0)"
                          >
                            <i
                              :class="`fa fa-${(!oTable || oTable.columns().visible()[columnIndex]) ? 'eye' : 'eye-slash'}`"
                            />
                            {{ $t(column.titleKey) }}
                          </a>
                        </li>
                      </template>
                    </ul>
                  </div>
                  <date-picker
                    v-model="dateRange"
                    :placeholder="$t('placeholder.chooseDateRage')"
                    format="DD/MM/YYYY"
                    range
                    :disabled-date="notBeforeToday"
                    :lang="$datepickerlocale[$i18n.locale]['lang']"
                  />
                </div>
              </div>
            </div>
            <div class="col-xs-4 col-sm-6 margin-bottom d-md-none d-sm-block">
              <div class="pull-right box-tools">
                <a
                  :href="`${baseUrl}/export/pending`"
                  class="btn btn-info btn-sm"
                >
                  <i class="fa fa-file-excel-o" /> {{ $t('label.exportAsExcel') }}
                </a>
              </div>
            </div>
          </div>
          <div class="col-md-12">
            <div>
              <table
                id="rejected-table"
                class="table table-hover table-striped table-bordered"
              >
                <tbody />
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
    <comment-modal
      :route-view-all-comments="{
        name: 'show-cancelled',
        params: {
          borrow_id: borrow_id
        }
      }"
    />
    <assign-loan-officer-modal @refreshList="oTable.draw()" />
    <reason-cancelled-list-modal />
  </div>
</template>

<script>
import { mapState } from 'vuex'
import CommentModal from '@/views/comments/components/CommentModal'
import AssignLoanOfficerModal from '@/views/borrows/_components/AssignLoanOfficerModal'
import ReasonCancelledListModal from '@/views/borrows/_components/ReasonCancelledListModal'

export default {
  name: 'List',
  components: { ReasonCancelledListModal, AssignLoanOfficerModal, CommentModal },
  metaInfo () {
    return {
      title: this.$t('menu.cancelled'),
      titleTemplate: '%s | ' + process.env.VUE_APP_NAME
    }
  },
  data () {
    return {
      baseUrl: process.env.VUE_APP_API,
      oTable: null,
      dateRange: null
    }
  },
  computed: {
    ...mapState({
      borrow_id: state => state.comment.borrow_id
    }),
    startDate () {
      if (this.dateRange && this.dateRange[0]) {
        return this.$moment(this.dateRange[0]).locale('en').format('YYYY-MM-DD')
      }
      return null
    },
    endDate () {
      if (this.dateRange && this.dateRange[1]) {
        return this.$moment(this.dateRange[1]).locale('en').format('YYYY-MM-DD')
      }
      return null
    },
    columns () {
      return [
        {
          data: 'action',
          name: 'action',
          searchable: false,
          orderable: false,
          title: this.$t('table.action'),
          titleKey: 'table.action',
          width: '1%'
        },
        {
          data: 'cancelled_at',
          name: 'cancelled_at',
          searchable: false,
          title: this.$t('table.cancelledAt'),
          titleKey: 'table.cancelledAt',
          width: '8%'
        },
        {
          data: 'loan_officers',
          name: 'loan_officers',
          title: this.$t('table.loan_officer'),
          titleKey: 'table.loan_officer',
          width: '8%',
          render: (data) => data || this.$t('string.na')
        },
        {
          data: 'borrow.national_id_number',
          name: 'borrow.national_id_number',
          title: this.$t('table.nationalIdNumber'),
          titleKey: 'table.nationalIdNumber',
          width: '10%',
          render: (data) => data || this.$t('string.na')
        },
        {
          data: 'borrow.name',
          name: 'borrow.name',
          title: this.$t('table.name'),
          titleKey: 'table.name',
          width: '8%'
        },
        {
          data: 'amount',
          name: 'amount',
          title: this.$t('table.amount'),
          titleKey: 'table.amount',
          width: '8%',
          render: (data) => `${this.currentFormat(data)}` || this.$t('string.na')
        },
        {
          data: 'duration',
          name: 'duration',
          title: this.$t('table.duration'),
          titleKey: 'table.duration',
          width: '8%',
          render: (data, type, row) => row.loan_type === 'Payday' ? `${data} Day(s)` : `${data} Month(s)`
        },
        {
          data: 'purpose_loan',
          name: 'purpose_loan',
          title: this.$t('table.type'),
          titleKey: 'table.type',
          width: '8%',
          render: (data) => data || this.$t('string.na')
        },
        {
          data: 'borrow.phone',
          name: 'borrow.phone',
          title: this.$t('table.phone'),
          titleKey: 'table.phone',
          width: '8%',
          render: (data) => data || this.$t('string.na')
        },
        {
          data: 'borrow.amount_applies',
          name: 'borrow.amount_applies',
          title: this.$t('table.amount_applies'),
          titleKey: 'table.amount_applies',
          width: '8%'
        }
      ]
    }
  },
  watch: {
    '$i18n.locale' () {
      this.oTable.destroy()
      this.oTable = this.dataTable()
      this.oTable.draw(true)
    },
    oTable () {
      this.enabledAction()
    },
    dateRange () {
      this.oTable.draw()
    }
  },
  methods: {
    notBeforeToday (date) {
      return date > new Date(new Date().setHours(0, 0, 0, 0))
    },
    toggleColumn (column) {
      column = this.oTable.column(column)
      column.visible(!column.visible())
    },
    dataTable () {
      const self = this
      return $('#rejected-table').DataTable({
        stateSave: true,
        processing: true,
        serverSide: true,
        scrollX: true,
        scrollCollapse: true,
        pageLength: process.env.VUE_APP_PAGE_LENGTH,
        lengthMenu: this.$lengthMenu,
        ajax: {
          method: 'POST',
          url: process.env.VUE_APP_API + '/api/v4/backend/cancelled/datatable',
          data: (d) => {
            d.status = 'Cancelled'
            d.commentLabel = self.$t('string.comment')
            d.assignLoanOfficerLabel = self.$t('string.assignLoanOfficer')
            d.labelEdit = self.$t('label.edit')
            d.labelToBeApplicant = self.$t('label.toBeApplicant')
            d.reasonCancellation = self.$t('string.reasonCancel')
            d.na = self.$t('string.na')
            d.startDate = self.startDate
            d.endDate = self.endDate
          },
          error: (xhr) => {
            this.onAccessDenied(xhr)
          }
        },
        columns: self.columns,
        order: [
          [1, 'desc']
        ],
        language: {
          url: `/locale/${this.$i18n.locale}.json`
        }
      })
    },
    enabledAction () {
      const self = this
      this.clearEventHandlers([
        '.btn-comment',
        '.btn-assign-loan-officer',
        '.borrow-to-be-applicant',
        '.borrow-edit',
        '.btn-reason-list'
      ])

      // comment
      $(document).on('click', '.btn-comment', function (e) {
        e.preventDefault()
        self.$store.commit('comment/openCommentModal')
        self.$store.commit('comment/setBorrowId', $(this).attr('data-borrow'))
      })
      // assign loan officer
      $(document).on('click', '.btn-assign-loan-officer', function (e) {
        e.preventDefault()
        self.$store.commit('loanOfficerAssignment/toggleAssignLoanOfficerModal')
        self.$store.commit('loanOfficerAssignment/setBorrowId', $(this).attr('data-borrow'))
      })
      // move to applicant list
      $(document).on('click', '.borrow-to-be-applicant', function (e) {
        e.preventDefault()
        self.revertToApplicant($(this).attr('data-borrow'))
      })
      // edit
      $(document).on('click', '.borrow-edit', function (e) {
        e.preventDefault()
        self.$router.push({
          name: 'edit-cancelled',
          params: {
            borrow_id: $(this).attr('data-borrow')
          }
        })
      })
      // open cancel history list modal
      $(document).on('click', '.btn-reason-list', function (e) {
        e.preventDefault()
        self.$store.commit('applicantCancel/toggleReasonCancelListModal')
        self.$store.commit('applicantCancel/setApplicableId', $(this).attr('data-borrow'))
      })
    },
    revertToApplicant (recordId) {
      const self = this
      this.$swal({
        title: self.$t('string.moveAsApplication'),
        text: self.$t('string.youWantToMoveTheClient?'),
        type: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: self.$t('string.yesIWantIt'),
        cancelButtonText: self.$t('button.cancel')
      }).then((result) => {
        if (result.value) {
          this.$axios.post(process.env.VUE_APP_API + '/api/v1/borrows/move-from-draft-to-applicant', { id: recordId })
            .then((response) => {
              if (response.data.code === 1) {
                this.oTable.draw(true)
                this.$swal({
                  title: self.$t('string.moved'),
                  text: self.$t('string.theRequestHaveBeenProceeded'),
                  type: 'success',
                  confirmButtonText: self.$t('button.ok')
                })
              }
              if (response.data.code === 0) {
                if (response.data.code === 0) {
                  this.$toastr('error', response.data.message, self.$t('string.moveApplicant'))
                }
              }
            })
            .catch((error) => {
              this.onResponseError(error)
            })
        }
      })
    }
  },
  mounted () {
    this.oTable = this.dataTable()
    this.enabledAction()
  },
  beforeDestroy () {
    this.oTable.clear()
    this.oTable.destroy()
  }
}
</script>

<style scoped>
#rejected-table tr td {
  height: 50px;
}
</style>
